import {GPMisc} from "./gp-misc.js";
import * as THREE from 'three';
import { OutlineEffect } from 'three/examples/jsm/effects/OutlineEffect.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

export let GP3DItemRenderer = {
    scene: null,
    camera: null,
    renderer: null,
    outlinePass: null,
    light: null,
    light2: null,
    loadedBufferGeometry: null,
    loadedURL: null,
    canvas: null,
    width: null,
    height: null,
    baseHeightCache: {},

    initScene: function () {

        GP3DItemRenderer.width = GPSettings.maxAllowedStructureRenderingWidth;
        GP3DItemRenderer.height = GPSettings.maxAllowedStructureRenderingHeight;

        GP3DItemRenderer.canvas = document.createElement('canvas');
        GP3DItemRenderer.canvas.width = GP3DItemRenderer.width;
        GP3DItemRenderer.canvas.height = GP3DItemRenderer.height;
        GP3DItemRenderer.canvas.style.display = "none";

        GP3DItemRenderer.scene = new THREE.Scene();
        GP3DItemRenderer.camera = new THREE.PerspectiveCamera(10, 1, 0.1, 2000);

        GP3DItemRenderer.renderer = new THREE.WebGLRenderer({canvas: GP3DItemRenderer.canvas, alpha: true, preserveDrawingBuffer: true, antialias: true});
        GP3DItemRenderer.renderer.setSize(GP3DItemRenderer.width, GP3DItemRenderer.height);
        GP3DItemRenderer.renderer.shadowMap.enabled = true;
        GP3DItemRenderer.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

        GP3DItemRenderer.outlinePass = new OutlineEffect(GP3DItemRenderer.renderer, {
            defaultThickness: 0.005,
            defaultColor: [ 0, 0, 0 ],
            defaultAlpha: 0.8
        });

        GP3DItemRenderer.light = new THREE.HemisphereLight( 0xf0f0ff, 0x505050, 1.8 );
        GP3DItemRenderer.scene.add(GP3DItemRenderer.light);

        GP3DItemRenderer.light2 = new THREE.PointLight(0xc5c5b5, 2.7);
        GP3DItemRenderer.light2.castShadow = true;
        GP3DItemRenderer.light2.shadow.radius = 20;
        GP3DItemRenderer.scene.add(GP3DItemRenderer.light2);

        GP3DItemRenderer.light2.shadow.mapSize.width = 2048;
        GP3DItemRenderer.light2.shadow.mapSize.height = 2048;
        GP3DItemRenderer.light2.shadow.camera.near = 1;
        GP3DItemRenderer.light2.shadow.camera.far = 2500;
    },

    scaleObject: function(gltf, height) {
        let bbox = new THREE.Box3().setFromObject(gltf.scene);

        let objheight = bbox.max.y - bbox.min.y;
        let s = height / objheight;

        gltf.scene.scale.set(gltf.scene.scale.x*s, gltf.scene.scale.y*s, gltf.scene.scale.z*s);

        bbox.setFromObject(gltf.scene);
        gltf.scene.translateX(-((bbox.max.x - bbox.min.x) / 2 + bbox.min.x));
        gltf.scene.translateZ(-((bbox.max.z - bbox.min.z) / 2 + bbox.min.z));
        gltf.scene.translateY(-bbox.min.y);
        bbox.setFromObject(gltf.scene);

        return Math.max(bbox.max.x - bbox.min.x, bbox.max.z - bbox.min.z, bbox.max.y - bbox.min.y);
    },

    getImageContext: function (img) {
        let can = document.createElement('canvas');
        can.width = GP3DItemRenderer.width;
        can.height = GP3DItemRenderer.height;
        let ctx = can.getContext('2d');
        ctx.drawImage(img, 0, 0);

        return {canvas: can, context: ctx};
    },

    setupCamera: function(rotation, maxdim) {
        GP3DItemRenderer.camera.position.x = 1200 * Math.cos( rotation );
        GP3DItemRenderer.camera.position.z = 1200 * Math.sin( rotation );
        GP3DItemRenderer.camera.position.y = 170;
        GP3DItemRenderer.camera.lookAt(0, 0, 0);
        GP3DItemRenderer.camera.fov = 4.2 * Math.atan( maxdim / ( 2 * 1200 ) ) * ( 180 / Math.PI );
        GP3DItemRenderer.camera.updateProjectionMatrix();
    },

    /**
     * Function to calculate visible height of the rendered object. This will be required for correcting png height
     * when rotating
     * @param {Object} gltf - rendered object
     * @param {int} maxdim - maximal object dimension in pixels (as caluclated by scaleObject)
     * @param {string} url - used as a cache key
     * @return {int} - height in pixels
     */
    calculateBaseHeight: function(gltf, maxdim, url) {
        if (GP3DItemRenderer.baseHeightCache[url])
            return GP3DItemRenderer.baseHeightCache[url];

        GP3DItemRenderer.scene.add(gltf.scene);
        GP3DItemRenderer.setupCamera(0, maxdim);

        GP3DItemRenderer.renderer.render(GP3DItemRenderer.scene, GP3DItemRenderer.camera);

        let ctx = GP3DItemRenderer.getImageContext(GP3DItemRenderer.renderer.domElement)['context'];
        GP3DItemRenderer.scene.remove(gltf.scene);

        //clipping
        let bounds = GPMisc.getContentBounds(ctx, GP3DItemRenderer.width, GP3DItemRenderer.height);
        GP3DItemRenderer.baseHeightCache[url] = bounds[3]-bounds[1];
        return bounds[3]-bounds[1];
    },

    /**
     * Render final rotated object
     *
     * @param {Object} gltf - object to render
     * @param {int} maxdim - maximal object dimension in pixels (as caluclated by scaleObject)
     * @param {int} rotation - angle of z-axis rotation
     * @param {int} baseVisualHeight - height in pixels of img when rotation=0
     * @param {boolean} outline - enable wireframe effect
     * @param {(1|-1)} lightDirection - switch shadow direction
     * @param {boolean=false} disableShadow - do not render ground shadow
     * @return {{img: HTMLCanvasElement, visualHeightFactor: number, xShift: number}} img - result on canvas, visualHeightFactor: scaling factor for png after rotation, xShift: how much is center shifted due to shadow
     */
    renderObject: function(gltf, maxdim, rotation, baseVisualHeight, outline, lightDirection, disableShadow) {
        lightDirection = lightDirection ? lightDirection : 1;

        GP3DItemRenderer.scene.add(gltf.scene);
        GP3DItemRenderer.setupCamera(rotation, maxdim);

        GP3DItemRenderer.light2.position.x = 700 * Math.cos( rotation + lightDirection * Math.PI / 6);
        GP3DItemRenderer.light2.position.z = 700 * Math.sin( rotation  + lightDirection *  Math.PI / 6);
        GP3DItemRenderer.light2.position.y = 1200;

        if (outline)
            GP3DItemRenderer.outlinePass.render(GP3DItemRenderer.scene, GP3DItemRenderer.camera);
        else
            GP3DItemRenderer.renderer.render(GP3DItemRenderer.scene, GP3DItemRenderer.camera);

        let res_noshade = GP3DItemRenderer.getImageContext(GP3DItemRenderer.renderer.domElement);

        //first clipping without shadow, to calculate dimension
        let ctx_noshade = res_noshade.context;
        let bounds_noshade = GPMisc.getContentBounds(ctx_noshade, GP3DItemRenderer.width, GP3DItemRenderer.height);

        let plane;
        if (!disableShadow) {
            const geometry = new THREE.PlaneGeometry(700, 700);
            const material = new THREE.ShadowMaterial({color: 0x000000});
            material.opacity = 0.2;
            plane = new THREE.Mesh(geometry, material);
            plane.rotation.x = 3 * Math.PI / 2;
            plane.receiveShadow = true;
            GP3DItemRenderer.scene.add(plane);
        }

        if (outline)
            GP3DItemRenderer.outlinePass.render(GP3DItemRenderer.scene, GP3DItemRenderer.camera);
        else
            GP3DItemRenderer.renderer.render(GP3DItemRenderer.scene, GP3DItemRenderer.camera);

        let res = GP3DItemRenderer.getImageContext(GP3DItemRenderer.renderer.domElement);

        GP3DItemRenderer.scene.remove(gltf.scene);
        if (!disableShadow) {
            GP3DItemRenderer.scene.remove(plane);
        }

        //clipping
        let ctx = res.context;
        let can = res.canvas;
        let bounds = GPMisc.getContentBounds(ctx, GP3DItemRenderer.width, GP3DItemRenderer.height);

        let mean_noshade = (bounds_noshade[2] + bounds_noshade[0])/2;
        let mean = (bounds[2] + bounds[0])/2;

        let cut = ctx.getImageData(bounds[0], bounds[1], bounds[2]-bounds[0], bounds[3]-bounds[1]);
        ctx.clearRect(0,0,can.width, can.height);
        can.width = bounds[2]-bounds[0];
        can.height = bounds[3]-bounds[1];
        ctx.putImageData(cut, 0, 0);

        return {
            "img": can,
            "visualHeightFactor": can.height/baseVisualHeight,
            "xShift" : mean - mean_noshade
        };
    },

    /**
     * Loads and renders object from glft 3d model to png
     * @param {string} url
     * @param {int} rotation - angle in rad
     * @param {int} height - in cm
     * @param {boolean} outline - enable wireframe effect
     * @param {(1|-1)} lightDirection - switch shadow direction
     * @param {boolean=false} disableShadow - do not render ground shadow
     * @return {{img: HTMLCanvasElement, visualHeightFactor: number, xShift :number}} img - result on canvas, visualHeightFactor: scaling factor for png after rotation, xShift: how much is center shifted due to shadow
     */
    renderObjectToCanvas: async function (url, rotation, height, outline, lightDirection, disableShadow) {
        let loaded = await GP3DItemRenderer.loadObject(url);
        let maxdim = GP3DItemRenderer.scaleObject(loaded, height);
        let baseVisualHeight = GP3DItemRenderer.calculateBaseHeight(loaded, maxdim, url);
        return GP3DItemRenderer.renderObject(loaded, maxdim, rotation, baseVisualHeight, outline, lightDirection, disableShadow);
    },

    loadObject: function(url) {
        return new Promise((resolve, reject) => {
            if (GP3DItemRenderer.loadedURL === url)
                return resolve(GP3DItemRenderer.loadedBufferGeometry)
            let loader = new GLTFLoader();
            loader.load(url, (gltf) => {
                gltf.scene.traverse( function( node ) {
                    if ( node instanceof THREE.Mesh ) {
                        node.castShadow = true;
                        node.receiveShadow = true;
                    }
                } );
                if (GP3DItemRenderer.loadedBufferGeometry)
                    GP3DItemRenderer.loadedBufferGeometry.scene.traverse((c) => {
                        if (c.material && c.material.map) c.material.map.dispose();
                        if (c.geometry) c.geometry.dispose();
                    });
                GP3DItemRenderer.loadedBufferGeometry = gltf;
                GP3DItemRenderer.loadedURL = url;
                resolve(gltf);
            },(xhr) => {}, reject);
        })
    }

}

document.addEventListener('DOMContentLoaded', GP3DItemRenderer.initScene, false);
